import { Card, Empty, Space, Table, Tag, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { inventoryApi } from '@/api/inventory';
import { PageHeader } from '@/shared/components/page-header';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function LowStockReportPage() {
  const { data: storeItems, isFetching: storeFetch } = useQuery({
    queryKey: ['inventory', 'store', 'items', 'low-stock'],
    queryFn: () => inventoryApi.storeLowStock(),
  });
  const { data: consumables, isFetching: consFetch } = useQuery({
    queryKey: ['inventory', 'consumables', 'items', 'low-stock'],
    queryFn: () => inventoryApi.consumablesLowStock(),
  });

  const totalLow = (storeItems?.length ?? 0) + (consumables?.length ?? 0);

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Reports"
        title="Low stock"
        description="Store items and consumables at or below their reorder thresholds."
        count={totalLow > 0 ? { value: totalLow, label: 'items low' } : undefined}
      />

      {totalLow === 0 && !storeFetch && !consFetch ? (
        <Empty description="Nothing is below threshold" />
      ) : (
        <>
          {(storeItems ?? []).length > 0 && (
            <Card title={<Space><WarningOutlined /> Store items</Space>} styles={{ body: { padding: 0 } }}>
              <Table
                rowKey="id"
                size="small"
                loading={storeFetch}
                dataSource={storeItems ?? []}
                pagination={false}
                columns={[
                  {
                    title: 'Item',
                    render: (_, row) => (
                      <Space orientation="vertical" size={0}>
                        <Typography.Text strong>{row.name}</Typography.Text>
                        {row.itemTypeName && <Tag>{row.itemTypeName}</Tag>}
                      </Space>
                    ),
                  },
                  { title: 'Stock', dataIndex: 'currentStock', width: 110, align: 'right', render: (v: string) => PKR(v) },
                  { title: 'Reorder at', dataIndex: 'reorderLevel', width: 110, align: 'right', render: (v: string) => PKR(v) },
                  { title: 'Cost (avg)', dataIndex: 'costPrice', width: 130, align: 'right', render: (v: string) => `PKR ${PKR(v)}` },
                ]}
              />
            </Card>
          )}

          {(consumables ?? []).length > 0 && (
            <Card title={<Space><WarningOutlined /> Consumables</Space>} styles={{ body: { padding: 0 } }}>
              <Table
                rowKey="id"
                size="small"
                loading={consFetch}
                dataSource={consumables ?? []}
                pagination={false}
                columns={[
                  {
                    title: 'Item',
                    render: (_, row) => (
                      <Space orientation="vertical" size={0}>
                        <Typography.Text strong>{row.name}</Typography.Text>
                        <Space size={6}>
                          {row.itemTypeName && <Tag>{row.itemTypeName}</Tag>}
                          <Tag>{row.unit}</Tag>
                        </Space>
                      </Space>
                    ),
                  },
                  { title: 'Stock', dataIndex: 'currentStock', width: 130, align: 'right', render: (v: string, row) => `${PKR(v)} ${row.unit}` },
                  { title: 'Threshold', dataIndex: 'lowStockThreshold', width: 110, align: 'right', render: (v: string) => PKR(v) },
                ]}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
